/**
 * Adapter - CardLifeWithdrawalGateway
 *
 * Envoie le retrait au backend CardLife qui orchestre l'appel CBS
 * et retourne le reçu PDF (Base64).
 *
 * Flow de retrait multi-signatures:
 * 1. POST /operation-retraits/complet → Initialisation (EN_ATTENTE)
 * 2. POST /{id}/signer → Signature 1, 2, ..., n-1 (EN_ATTENTE)
 * 3. POST /{id}/signer → Signature n (EXECUTEE + PDF)
 */

import { buildBearerAuthHeader } from '../http/auth';
import { messageErreurApi } from '../http/erreurs';

const ENV = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {};
const CARDLIFE_BASE_URL = (ENV.VITE_CARDLIFE_BASE_URL ?? '').replace(/\/+$/g, '');
const WITHDRAWAL_ENDPOINT =
  ENV.VITE_CARDLIFE_WITHDRAWAL_ENDPOINT ?? '/api/v1/biotransac/operation-retraits';

// Types pour le retrait
export interface RetraitGlobalDTO {
  operation: {
    id: number;
    reference: string;
    codeOpe: string;
    montant: number;
    devise: string;
    sens: 'DEBIT' | 'CREDIT';
    dateOperation: string;
    dateEnregistrement: string;
    response: string;
    message: string;
    numcpt: string;
    codeuser: string;
    codeOperationId: number;
  };
  billetage: {
    id: number;
    dixmille: number;
    cinqmille: number;
    deuxmille: number;
    mille: number;
    cinqcent: number;
    deuxcentcinquante: number;
    deuxcent: number;
    cent: number;
    cinquante: number;
    vingtcinq: number;
    dix: number;
    cinq: number;
    un: number;
    reference: string;
    nombreTotal: number;
    montantTotal: number;
    dateComptage: string;
    operationVersementId: number;
    operationRetraitId: number;
  };
  intitule: string;
  nbrsignataire: number; // Nombre de signatures requises
  codeuser: string;
  motifRetrait?: string;
}

export interface SignatureRetraitDTO {
  operationRetraitId: number;
  carteId: number;
  carteNumero: string;
  ordre: number;
  pinVerifie: boolean;
  empreinteVerifiee: boolean;
}

/** Reçu construit par le backend : le compte est celui réellement débité au CBS. */
export interface RecuOperationDTO {
  reference: string;
  dateOperation: string;
  typeOperation: string;
  montant: number;
  devise: string;
  numcpt: string;
  intituleCompte?: string | null;
  agence?: string | null;
  motif?: string | null;
  codeCaissier?: string | null;
  signataires: { ordre: number; nom: string; carteMasquee?: string | null }[];
}

export interface RetraitResponseDTO {
  retrait: {
    id: number;
    operationId: number;
    intitule: string;
    nbrsignataire: number;
    nbrsignaturesEffectives: number;
    statut: 'EN_ATTENTE' | 'EXECUTEE' | 'ANNULEE';
    dateExpiration: string | null;
    codeuser: string;
  };
  pdfBase64?: string; // Présent uniquement sur la dernière signature
  /** Données du reçu, présentes quand le retrait est exécuté au CBS */
  recu?: RecuOperationDTO;
  externalReference?: string;
  /** Référence opération générée par le backend (ex: BIO-011-1772551406-79500FEF) */
  operationReference?: string;
}

/**
 * Mapper le billetage du format frontend vers le format backend
 */
function mapDenominationToBilletage(denomination: Record<number, number>) {
  const get = (value: number) => {
    const v = denomination[value];
    return typeof v === 'number' && Number.isFinite(v) ? v : 0;
  };

  const billetage = {
    id: null as unknown as number,
    dixmille: get(10000),
    cinqmille: get(5000),
    deuxmille: get(2000),
    mille: get(1000),
    cinqcent: get(500),
    deuxcentcinquante: get(250),
    deuxcent: get(200),
    cent: get(100),
    cinquante: get(50),
    vingtcinq: get(25),
    dix: get(10),
    cinq: get(5),
    un: get(1),
    reference: '',
    nombreTotal: 0,
    montantTotal: 0,
    dateComptage: new Date().toISOString(),
    operationVersementId: null as unknown as number,
    operationRetraitId: null as unknown as number,
  };

  billetage.nombreTotal =
    billetage.dixmille +
    billetage.cinqmille +
    billetage.deuxmille +
    billetage.mille +
    billetage.cinqcent +
    billetage.deuxcentcinquante +
    billetage.deuxcent +
    billetage.cent +
    billetage.cinquante +
    billetage.vingtcinq +
    billetage.dix +
    billetage.cinq +
    billetage.un;

  billetage.montantTotal =
    billetage.dixmille * 10000 +
    billetage.cinqmille * 5000 +
    billetage.deuxmille * 2000 +
    billetage.mille * 1000 +
    billetage.cinqcent * 500 +
    billetage.deuxcentcinquante * 250 +
    billetage.deuxcent * 200 +
    billetage.cent * 100 +
    billetage.cinquante * 50 +
    billetage.vingtcinq * 25 +
    billetage.dix * 10 +
    billetage.cinq * 5 +
    billetage.un * 1;

  return billetage;
}

/**
 * Recherche le PDF Base64 dans la réponse du backend
 */
function findPdfBase64(value: any): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') {
    return value.length > 200 ? value : undefined;
  }
  if (typeof value !== 'object') return undefined;

  const direct =
    value.pdfBase64 ??
    value.pdf_base64 ??
    value.pdf ??
    value.receiptPdfBase64 ??
    value.recuBase64 ??
    value.recu ??
    value.documentBase64 ??
    value.document;

  if (typeof direct === 'string') return direct;

  for (const key of Object.keys(value)) {
    const nested = (value as any)[key];
    if (nested && typeof nested === 'object') {
      const hit = findPdfBase64(nested);
      if (hit) return hit;
    }
  }

  return undefined;
}

export class CardLifeWithdrawalGateway {
  /**
   * Initialise un retrait (étape 1)
   * POST /operation-retraits/complet
   */
  async initializeWithdrawal(input: {
    accountNumber: string;
    amount: number;
    denomination: Record<number, number>;
    nbSignatures: number;
    cashierId: string;
    notes?: string;
    motifRetrait?: string;
  }): Promise<RetraitResponseDTO> {
    const authHeader = buildBearerAuthHeader();
    const url = `${CARDLIFE_BASE_URL}${WITHDRAWAL_ENDPOINT}/complet`;

    const billetage = mapDenominationToBilletage(input.denomination);

    const payload: RetraitGlobalDTO = {
      operation: {
        id: null as unknown as number,
        reference: '',
        codeOpe: '01110',            // Code opération retrait CBS
        montant: input.amount,
        devise: 'XOF',
        sens: 'DEBIT',
        dateOperation: new Date().toISOString(),
        dateEnregistrement: new Date().toISOString(),
        response: '',
        message: '',
        numcpt: input.accountNumber,
        codeuser: input.cashierId,
        codeOperationId: 10,          // ID code_operation pour retrait
      },
      billetage,
      intitule: input.notes ?? 'Retrait espèces',
      nbrsignataire: input.nbSignatures,
      codeuser: input.cashierId,
      motifRetrait: input.motifRetrait ?? input.notes ?? 'Retrait espèces',
    };

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(authHeader ? { Authorization: authHeader } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(messageErreurApi(response.status, text, "Le retrait n'a pas pu être initialisé."));
    }

    const json = await response.json();

    // Extraire la référence opération depuis la réponse backend
    // Le backend peut la retourner à différents endroits selon le DTO
    const operationReference =
      json.operationReference ??
      json.operation?.reference ??
      json.retrait?.operationReference ??
      json.reference ??
      undefined;

    return {
      ...json,
      operationReference,
    } as RetraitResponseDTO;
  }

  /**
   * Ajoute une signature (étapes 2 à n)
   * POST /operation-retraits/{id}/signer
   */
  async addSignature(
    retraitId: number,
    signature: {
      cardNumber: string;
      ordre: number;
      pinVerified: boolean;
      fingerprintVerified: boolean;
    }
  ): Promise<RetraitResponseDTO> {
    const authHeader = buildBearerAuthHeader();
    const url = `${CARDLIFE_BASE_URL}${WITHDRAWAL_ENDPOINT}/${retraitId}/signer`;

    // Note: Il faut mapper le cardNumber vers carteId
    // En production, vous devrez faire un appel API pour récupérer l'ID de la carte
    const payload: SignatureRetraitDTO = {
      operationRetraitId: retraitId,
      carteId: 0, // À récupérer depuis l'API /cartes/by-numero/{cardNumber}
      carteNumero: signature.cardNumber,
      ordre: signature.ordre,
      pinVerifie: signature.pinVerified,
      empreinteVerifiee: signature.fingerprintVerified,
    };

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(authHeader ? { Authorization: authHeader } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(messageErreurApi(response.status, text, 'La validation du retrait a échoué.'));
    }

    const json = await response.json();

    // Extraire le PDF si présent (dernière signature)
    const pdfBase64 = findPdfBase64(json);

    // Extraire la référence opération depuis la réponse backend
    const operationReference =
      json.operationReference ??
      json.operation?.reference ??
      json.retrait?.operationReference ??
      json.reference ??
      undefined;

    return {
      ...json,
      pdfBase64,
      operationReference,
    } as RetraitResponseDTO;
  }

  /**
   * Consulte l'état d'un retrait
   * GET /operation-retraits/{id}
   */
  async getWithdrawalStatus(retraitId: number): Promise<RetraitResponseDTO> {
    const authHeader = buildBearerAuthHeader();
    const url = `${CARDLIFE_BASE_URL}${WITHDRAWAL_ENDPOINT}/${retraitId}`;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(authHeader ? { Authorization: authHeader } : {}),
      },
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Échec récupération retrait (${response.status})`);
    }

    const json = await response.json();
    return json as RetraitResponseDTO;
  }

  /**
   * Liste les signatures d'un retrait
   * GET /operation-retraits/{id}/signatures
   */
  async getSignatures(retraitId: number): Promise<SignatureRetraitDTO[]> {
    const authHeader = buildBearerAuthHeader();
    const url = `${CARDLIFE_BASE_URL}${WITHDRAWAL_ENDPOINT}/${retraitId}/signatures`;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(authHeader ? { Authorization: authHeader } : {}),
      },
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Échec récupération signatures (${response.status})`);
    }

    const json = await response.json();
    return json as SignatureRetraitDTO[];
  }
}
